import axios from 'axios'
import { ElMessage, ElLoading } from 'element-plus'
import { generateSign, getSignSecret } from '@/utils/sign'

/**
 * 获取完整 API URL
 * 将 {method} 占位符替换为实际的接口路径
 */
function getApiUrl(method) {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/{method}'
  return baseUrl.replace('{method}', method)
}

/**
 * 全局 Loading 控制
 * - 支持并发请求时的正确显示/隐藏
 * - 可通过请求参数 { _hideLoading: true } 禁用 loading
 */
let loadingInstance = null
let loadingCount = 0

/**
 * 错误提示去重
 * - 并发请求同时失败时，相同错误只弹一次提示，避免满屏重复弹窗
 * - 对「请求已过期」类时间问题，自动转换为白话引导用户校准电脑时间
 */
const recentErrors = new Map()
const DEDUP_MS = 5000 // 5秒内相同错误不重复弹出

function showErrorOnce(msg, options = {}) {
  const now = Date.now()
  const lastShown = recentErrors.get(msg) || 0
  if (now - lastShown < DEDUP_MS) return
  recentErrors.set(msg, now)
  ElMessage.error({ message: msg, duration: 8000, ...options })
}

function showLoading() {
  if (loadingCount === 0) {
    loadingInstance = ElLoading.service({
      lock: true,
      text: '加载中...',
      background: 'rgba(255, 255, 255, 0.7)',
    })
  }
  loadingCount++
}

function hideLoading() {
  loadingCount--
  if (loadingCount <= 0) {
    loadingCount = 0
    if (loadingInstance) {
      loadingInstance.close()
      loadingInstance = null
    }
  }
}

/**
 * 获取资源 URL
 */
export function getResUrl(path) {
  if (!path) return ''
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  const resBase = import.meta.env.VITE_RES_BASE_URL || ''
  return resBase + path
}

/**
 * 获取上传 API URL
 * 统一走 /api/ 网关，与 nginx 反向代理配置一致
 */
export function getUploadUrl(method) {
  const uploadBase = import.meta.env.VITE_UPLOAD_API_URL || '/api/upload/{method}'
  return uploadBase.replace('{method}', method)
}

// 创建 axios 实例
const service = axios.create({
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'X-Device-Type': 'pc',
  },
})

// 请求拦截器：自动附加 token 到请求参数中
service.interceptors.request.use(
  (config) => {
    // 显示 loading（除非明确指定隐藏）
    if (!config.data?._hideLoading) {
      showLoading()
    }

    // 从 localStorage 提取 token（保持与原系统兼容，token 在请求参数中传递）
    const loginDataStr = localStorage.getItem('login_data')
    if (loginDataStr) {
      try {
        const loginData = JSON.parse(loginDataStr)
        if (loginData && loginData.token) {
          if (config.data && typeof config.data === 'object') {
            config.data.token = loginData.token
            // authorization 同时放入 body 和 Header，参与后端签名校验
            config.data.authorization = loginData.token
          } else {
            config.data = { token: loginData.token, authorization: loginData.token }
          }
          // 设置 HTTP Header，后端 SignMiddleware 从 Header 读取参与签名
          config.headers['Authorization'] = loginData.token
        }
      } catch {
        // ignore parse error
      }
    }

    // 参数签名：注入时间戳防重放，然后对完整 body 生成签名
    const signSecret = getSignSecret()
    if (signSecret && config.data && typeof config.data === 'object') {
      config.data.timestamp = Math.floor(Date.now() / 1000)
      config.data.sign = generateSign(config.data, signSecret)
    }

    return config
  },
  (error) => {
    hideLoading()
    return Promise.reject(error)
  },
)

// 响应拦截器：统一处理返回结果
service.interceptors.response.use(
  (response) => {
    hideLoading()
    const res = response.data

    // 兼容两种错误码格式：resultCode(0=成功) 和 code(200=成功)
    let isSuccess = true
    let errorCode = '0'
    if (res.resultCode !== undefined) {
      errorCode = String(res.resultCode)
      isSuccess = errorCode === '0'
    } else if (res.code !== undefined) {
      errorCode = String(res.code)
      isSuccess = errorCode === '200'
    }
    const errorMsg = res.resultMsg || res.message || ''

    if (!isSuccess) {
      // token 失效：错误码100 或 错误消息包含令牌相关关键词
      const isTokenExpired = errorCode === '100' || /令牌|token/i.test(errorMsg)
      if (isTokenExpired) {
        localStorage.clear()
        window.location.replace('#/login')
        window.location.reload()
        return Promise.reject(new Error('登录失效'))
      }

      // 密码已被修改：需要重新登录
      const isPasswordChanged = /密码已被修改|请重新登录/.test(errorMsg)
      if (isPasswordChanged) {
        localStorage.clear()
        ElMessage.warning('您的密码已被修改，请重新登录')
        window.location.replace('#/login')
        window.location.reload()
        return Promise.reject(new Error('密码已被修改'))
      }

      // 其他错误（去重 + 白话化）
      if (errorMsg) {
        // 时间不同步导致的签名过期：转为白话引导用户自行校准电脑时间
        if (/请求已过期|过期.*刷新/.test(errorMsg)) {
          showErrorOnce(
            '提交失败：您的电脑时间可能不准确。' +
            '请右键点击屏幕右下角的时间，选择"调整日期和时间"，' +
            '打开"自动设置时间"后等待几秒钟同步，再重新操作。'
          )
        } else {
          showErrorOnce(errorMsg)
        }
      }
      return Promise.reject(new Error(errorMsg || '请求失败'))
    }

    return res.data
  },
  (error) => {
    hideLoading()
    if (error.message !== '登录失效') {
      showErrorOnce('网络不太稳定，请稍等片刻再试一次')
    }
    return Promise.reject(error)
  },
)

/**
 * 统一请求函数
 * @param {string} method - 接口路径（如 'account/loginByPhone'）
 * @param {object} data - 请求参数
 * @returns {Promise}
 */
export function request(method, data = {}) {
  return service.post(getApiUrl(method), data)
}

/** 文件下载请求（blob 流式返回，取消超时限制，适配大文件） */
export function requestBlob(method, data = {}) {
  return service.post(getApiUrl(method), data, { responseType: 'blob', timeout: 0 })
}

export default service
